import { DirectionalHint, IconButton, TooltipHost } from "@fluentui/react";
import styles from "./UserChatMessage.module.css";
import React, { forwardRef, useEffect, useImperativeHandle, useState, useMemo, useCallback } from "react";
import {
    ChatAppResponse,
    calculateVersions,
    getIdToCheck,
    AppType,
    QuestionDetails,
    highlightTextAsHtml,
    skipUnwantedTag,
    decodeHtmlEntities,
    escapeHtml
} from "../../api";
import i18next from "i18next";
import { userInfo } from "../../authConfig";
import { ContentCopy } from "@mui/icons-material";
import { Alert, Snackbar, Stack, Tooltip } from "@mui/material";
import { QuestionInputReducerAction } from "../../Store/QuestionInputReducer";
import { useDispatch } from "react-redux";
import { Attachment } from "../FileAttachments/FileAttachments";
import { ChatAreaFileRender } from "../FileAttachments/ChatAreaFileRender";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import MuiIconButton from "@mui/material/IconButton";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { isPartialAttachmentNameMatch, normalizeAttachmentName } from "../../utils/AttachmentNameUtils";

interface Props {
    message: string;
    onSend?: (question: string, appType: string, index: number | undefined) => void;
    index?: number;
    answers?: [user: QuestionDetails, response: { FAQ?: ChatAppResponse; Manuals?: ChatAppResponse; ALL?: ChatAppResponse }][];
    onEditClick?: (checked: boolean, info: QuestionDetails) => void;
    IsOnEdit?: boolean;
    isAdminUser?: boolean;
    selectedQuestion?: boolean;
    applyRestriction?: boolean;
    reference?: any;
    responseGenerationActive?: boolean;
    selectedAppType: AppType;
    templateId: string | undefined;
    questionContent: string;
    sourcePage: string;
    fileAttachments: Array<Attachment>;
    searchTerm: string;
    setSelectedAppType?: (appType: AppType) => void;
    currentVersionIndex: number | undefined | null;
    onVersionChange?: (index: number, parentMessageId: string, answerIndex: number) => void;
    histories?: any;
    onExpandToggle?: () => void;
    isExpanded?: boolean;
}

interface QuestionInfoToolTipWithCopyOptionProps {
    questionContextTextValue: string;
}

export interface UserChatMessageRef {
    handleEditClickForAbortedChatAPIRequest: (index: number) => void;
    openAttachmentPreviewByName: (filename: string, page?: number) => void;
}

export const UserChatMessage = forwardRef<UserChatMessageRef, Props>(
    (
        {
            onSend,
            message,
            index,
            answers,
            onEditClick,
            IsOnEdit,
            isAdminUser,
            selectedQuestion,
            applyRestriction,
            reference,
            responseGenerationActive,
            selectedAppType,
            templateId,
            questionContent,
            sourcePage,
            fileAttachments,
            searchTerm,
            setSelectedAppType,
            currentVersionIndex,
            onVersionChange,
            histories,
            onExpandToggle,
            isExpanded
        }: Props,
        ref
    ) => {
        const [openReq, setOpenReq] = useState<{ index: number; nonce: number; page?: number } | undefined>(undefined);
        const [isEdit, setIsEdit] = useState(IsOnEdit);
        const [editedMessage, setEditedMessage] = useState<string>(message);
        const [isCheckIconDisabled, setIsCheckIconDisabled] = useState(true);
        const [initialMessage, setInitialMessage] = useState(message);
        const dispatch = useDispatch();

        // Memoize computed values to prevent unnecessary recalculations
        const questionInfo = useMemo(() => (typeof index === "number" && answers?.[index]?.[0] ? answers[index][0] : undefined), [index, answers]);

        const idToCheck = useMemo(() => getIdToCheck(questionInfo), [questionInfo]);
        const totalVersions = useMemo(() => calculateVersions(histories, idToCheck), [histories, idToCheck]);
        const historyEntries = useMemo(() => (histories ? Object.keys(histories) : []), [histories]);
        const isVersionAvailable = useMemo(
            () => Array.isArray(historyEntries) && historyEntries.includes(idToCheck) && totalVersions > 1,
            [historyEntries, idToCheck, totalVersions]
        );
        useEffect(() => {
            setEditedMessage(message);
        }, [message]);

        useEffect(() => {
            setIsEdit(false);
        }, [applyRestriction]);

        useEffect(() => {
            const messageContentFlag =
                !editedMessage ||
                editedMessage.trim().length === 0 ||
                String(editedMessage).replace(/^\s+/, "").trim() === String(initialMessage).replace(/^\s+/, "").trim();

            if (isEdit && index && Number.isInteger(index) && answers?.[index]?.[1] && selectedAppType !== "ALL") {
                let appNameValue: AppType;
                if (answers[index][1]?.[selectedAppType]) {
                    appNameValue = selectedAppType;
                } else {
                    appNameValue = selectedAppType === "FAQ" ? "Manuals" : "FAQ";
                }
                const productFilterValue = answers[index][1]?.[appNameValue]?.context?.product_filter ?? null;
                if (productFilterValue) {
                    const disableCheckIconBasedOnProduct =
                        (selectedAppType === "Manuals" && !productFilterValue.manual_key) || (selectedAppType === "FAQ" && !productFilterValue.faq_key);
                    setIsCheckIconDisabled(messageContentFlag || disableCheckIconBasedOnProduct);
                    return;
                }
            }
            setIsCheckIconDisabled(messageContentFlag);
        }, [editedMessage, initialMessage, index, answers, selectedAppType]);

        // Memoize callback functions
        const handleEditClick = useCallback(
            async (index: number) => {
                if (onEditClick && questionInfo) {
                    setIsEdit(true);
                    onEditClick(true, questionInfo);
                    if (questionInfo?.extractedQuestion) {
                        setEditedMessage(questionInfo?.extractedQuestion ?? "");
                        setInitialMessage(questionInfo?.extractedQuestion ?? "");
                    }
                    setIsCheckIconDisabled(true);
                }
            },
            [onEditClick, questionInfo]
        );

        useImperativeHandle(
            ref,
            () => ({
                handleEditClickForAbortedChatAPIRequest: index => handleEditClick(index),
                openAttachmentPreviewByName: (filename: string, page?: number) => {
                    if (!fileAttachments || fileAttachments.length === 0) return;
                    const normalizeForPreviewMatch = (value?: string) =>
                        normalizeAttachmentName(value, {
                            trimTrailingPunctuation: true
                        });
                    const requested = normalizeForPreviewMatch(filename);
                    let idx = fileAttachments.findIndex(att => normalizeForPreviewMatch(att.file?.name || "") === requested);
                    if (idx < 0 && requested) {
                        idx = fileAttachments.findIndex(att => isPartialAttachmentNameMatch(normalizeForPreviewMatch(att.file?.name || ""), requested));
                    }

                    if (idx >= 0) {
                        setOpenReq({ index: idx, nonce: Date.now(), page });
                    }
                }
            }),
            [handleEditClick, fileAttachments]
        );

        const handleCheckClick = async (index: number | undefined) => {
            setIsEdit(false);
            setSelectedAppType?.(index === 0 ? "ALL" : selectedAppType);
            dispatch(QuestionInputReducerAction.updateRequestTriggerType("QuestionEdit"));

            answers ? answers.splice(index as number) : [];
            if (onSend) {
                onSend(editedMessage, "FAQ", index);
            }
        };

        const QuestionInfoToolTipWithCopyOption: React.FC<QuestionInfoToolTipWithCopyOptionProps> = ({ questionContextTextValue }) => {
            const [isTooltipVisible, setTooltipVisible] = useState(false);
            const [toastVisible, setToastVisible] = useState(false);
            const plainTextForClipboard = questionContextTextValue?.replace(/^\s+/, "") ?? "";
            const escapedQuestionText = skipUnwantedTag(escapeHtml(plainTextForClipboard).replace(/\n/g, "<br/>"));
            const handleInfoButtonClick = () => {
                setTooltipVisible(true);
            };

            const copyQuestionToClipBoard = (question: string) => {
                navigator.clipboard.writeText(question).then(() => {
                    setToastVisible(true);
                    setTimeout(() => {
                        setTooltipVisible(false);
                    }, 1500);
                });
            };
            return (
                <TooltipHost
                    content={
                        <>
                            <div className={styles.tooltipCopyButton}>
                                <IconButton
                                    title={i18next.t("chat.templateCopyButton")}
                                    className={styles.copyIcon}
                                    onClick={e => {
                                        e.preventDefault();
                                        copyQuestionToClipBoard(plainTextForClipboard);
                                    }}
                                >
                                    <ContentCopy className={styles.copyIcon} />
                                </IconButton>
                            </div>
                            <div className={styles.message} dangerouslySetInnerHTML={{ __html: escapedQuestionText }}></div>
                        </>
                    }
                    directionalHint={DirectionalHint.bottomCenter}
                    calloutProps={{
                        isBeakVisible: true,
                        preventDismissOnLostFocus: true,
                        shouldDismissOnWindowFocus: false,
                        onDismiss: e => {
                            e?.preventDefault();
                        }
                    }}
                    hidden={!isTooltipVisible}
                >
                    <IconButton
                        title={i18next.t("chat.templateInfoButton")}
                        className={styles.infoButton}
                        iconProps={{ iconName: "Info" }}
                        onClick={handleInfoButtonClick}
                    />
                    <Snackbar open={toastVisible} autoHideDuration={3000} onClose={() => setToastVisible(false)}>
                        <Alert onClose={() => setToastVisible(false)} severity="success" variant="filled" sx={{ width: "100%" }}>
                            {i18next.t("chat.templateCopySuccess")}
                        </Alert>
                    </Snackbar>
                </TooltipHost>
            );
        };

        const CopyQuestionButton: React.FC<{
            text: string;
            size?: "small" | "medium" | "large";
            onCopied?: () => void;
        }> = ({ text, size = "small", onCopied }) => {
            const [copied, setCopied] = React.useState(false);

            const doCopy = async () => {
                try {
                    if (navigator.clipboard?.writeText) {
                        await navigator.clipboard.writeText(text);
                    } else {
                        const ta = document.createElement("textarea");
                        ta.value = text;
                        ta.style.position = "fixed";
                        ta.style.opacity = "0";
                        document.body.appendChild(ta);
                        ta.focus();
                        ta.select();
                        document.execCommand("copy");
                        document.body.removeChild(ta);
                    }
                    setCopied(true);
                    onCopied?.();
                } catch {
                    console.error("Failed to copy text");
                    setCopied(false);
                }
            };

            return (
                <>
                    <Tooltip title={i18next.t("chat.copyButton")}>
                        <MuiIconButton aria-label="copy question" size={size} color="primary" onClick={doCopy}>
                            <ContentCopyIcon fontSize="inherit" />
                        </MuiIconButton>
                    </Tooltip>
                    <Snackbar
                        open={copied}
                        autoHideDuration={1200}
                        onClose={() => setCopied(false)}
                        message={i18next.t("chat.copied")}
                        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    />
                </>
            );
        };

        const handleVersionNavigation = (direction: "prev" | "next") => {
            if (currentVersionIndex != null && totalVersions) {
                let newIndex = currentVersionIndex;
                if (direction === "prev" && currentVersionIndex > 0) {
                    newIndex = currentVersionIndex - 1;
                } else if (direction === "next" && currentVersionIndex < totalVersions) {
                    newIndex = currentVersionIndex + 1;
                }
                if (newIndex !== currentVersionIndex) {
                    if (onVersionChange) {
                        onVersionChange(newIndex, idToCheck, index as number);
                    }
                }
            }
        };

        return (
            <>
                {fileAttachments && fileAttachments.length > 0 && (
                    <div className={styles.filesRenderContainer}>
                        <ChatAreaFileRender files={fileAttachments} openRequest={openReq} />
                    </div>
                )}

                <div className={styles.container}>
                    {selectedQuestion && isEdit && IsOnEdit ? (
                        <div key={index}>
                            <textarea
                                key={index}
                                value={String(editedMessage).replace(/^\s+/, "")}
                                className={`${styles.message} ${styles.editTextarea}`}
                                style={reference?.current?.offsetWidth ? { width: `${reference?.current?.offsetWidth}px` } : undefined}
                                onChange={e => {
                                    setEditedMessage(e.target.value);
                                }}
                                aria-label="Edit message"
                            ></textarea>
                            <div className={styles.editButtonContainer}>
                                <IconButton
                                    disabled={isCheckIconDisabled}
                                    title={i18next.t("chat.save")}
                                    iconProps={{ iconName: "CheckMark" }}
                                    onClick={() => {
                                        handleCheckClick(index);
                                    }}
                                />
                                <span>
                                    <IconButton
                                        title={i18next.t("chat.cancel")}
                                        iconProps={{ iconName: "cancel" }}
                                        onClick={() => {
                                            setIsEdit(false);
                                            setEditedMessage(message);
                                            if (onEditClick && questionInfo) onEditClick(false, questionInfo);
                                        }}
                                    />
                                </span>
                            </div>
                        </div>
                    ) : (
                        <div key={index} className={styles.messageWrapper}>
                            <div
                                className={`${styles.message} ${styles.messageDisplay}`}
                                dangerouslySetInnerHTML={{
                                    __html: skipUnwantedTag(
                                        highlightTextAsHtml(escapeHtml(editedMessage?.replace(/^\s+/, "") ?? "").replace(/\n/g, "<br/>"), searchTerm)
                                    )
                                }}
                            />
                            <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                justifyContent="flex-end"
                                sx={{
                                    width: "100%"
                                }}
                            >
                                <Stack className={styles.hoverActions} direction="row" spacing={1} alignItems="center" sx={{ width: "fit-content" }}>
                                    {sourcePage === "chat" && onExpandToggle && (
                                        <Tooltip
                                            title={
                                                isExpanded
                                                    ? i18next.t("chat.collapseQA", { defaultValue: "Collapse full view" })
                                                    : i18next.t("chat.expandQA", { defaultValue: "Expand full view" })
                                            }
                                        >
                                            <span>
                                                <MuiIconButton
                                                    color="primary"
                                                    size="small"
                                                    aria-label={isExpanded ? "collapse" : "expand"}
                                                    onClick={onExpandToggle}
                                                >
                                                    {isExpanded ? <CloseFullscreenIcon fontSize="small" /> : <OpenInFullIcon fontSize="small" />}
                                                </MuiIconButton>
                                            </span>
                                        </Tooltip>
                                    )}
                                    {sourcePage === "chat" && (
                                        <Tooltip title={i18next.t("chat.edit")}>
                                            <span>
                                                <MuiIconButton
                                                    color="primary"
                                                    size="small"
                                                    aria-label="edit"
                                                    onClick={() => index !== undefined && handleEditClick(index)}
                                                    disabled={applyRestriction || responseGenerationActive}
                                                >
                                                    <EditOutlinedIcon fontSize="small" />
                                                </MuiIconButton>
                                            </span>
                                        </Tooltip>
                                    )}
                                    {(sourcePage === "chat" || sourcePage === "feedback") &&
                                        (isAdminUser || String(userInfo?.isWap).toLowerCase() === "true") &&
                                        !!templateId &&
                                        templateId !== "None" && <QuestionInfoToolTipWithCopyOption questionContextTextValue={questionContent} />}
                                    <CopyQuestionButton text={editedMessage} />
                                </Stack>
                                {isVersionAvailable && (
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                        <Tooltip title={i18next.t("answer.previousVersion")}>
                                            <span>
                                                <MuiIconButton
                                                    color="primary"
                                                    size="small"
                                                    onClick={() => handleVersionNavigation("prev")}
                                                    disabled={applyRestriction || responseGenerationActive || currentVersionIndex === 0}
                                                >
                                                    <KeyboardArrowLeftIcon fontSize="small" />
                                                </MuiIconButton>
                                            </span>
                                        </Tooltip>
                                        <span className={styles.versionCounter}>
                                            {(currentVersionIndex ?? 0) + 1}/{totalVersions}
                                        </span>
                                        <Tooltip title={i18next.t("answer.nextVersion")}>
                                            <span>
                                                <MuiIconButton
                                                    color="primary"
                                                    size="small"
                                                    onClick={() => handleVersionNavigation("next")}
                                                    disabled={applyRestriction || responseGenerationActive || (currentVersionIndex ?? 0) + 1 === totalVersions}
                                                >
                                                    <KeyboardArrowRightIcon fontSize="small" />
                                                </MuiIconButton>
                                            </span>
                                        </Tooltip>
                                    </Stack>
                                )}
                            </Stack>
                        </div>
                    )}
                </div>
            </>
        );
    }
);

UserChatMessage.displayName = "UserChatMessage";
